import React, { useState, useEffect, useCallback } from 'react';
import './BSpacyHub.css'; 

// --- Assets ---
// ── POR QUE VIDEO Y NO GIF ──────────────────────────────────────────────────
// Los gifs de Spacy son de 1080x1080 y pesan 44 MB entre todos; el de hablar,
// el solo, 26,5 MB. Medido en esta misma pagina: un visitante que entraba desde
// el movil se descargaba 45 MB antes de ver a Spacy moverse. Eso no es un
// problema de rendimiento, es la primera impresion de NSL.
//
// Los mismos videos a 512 px pesan 636 KB entre todos —72 veces menos— y a
// tamano de pantalla se ven igual. Un gif no se puede comprimir de verdad
// porque no comprime entre fotogramas: guarda cada cuadro casi entero.
//
// Los .gif siguen en assets/ por si hiciera falta volver.
import SpacyNormal from '../../assets/spacy-normal.mp4';
import SpacyHablando from '../../assets/spacy-hablando.mp4';
import SpacyPensando from '../../assets/spacy-pensando.mp4';
import SpacyError from '../../assets/spacy-error.mp4';

const useBrowserTTS = () => {
  const [speaking, setSpeaking] = useState(false);
  const [spanishVoice, setSpanishVoice] = useState(null);

  useEffect(() => {
    const getVoices = () => {
      const voices = window.speechSynthesis.getVoices();
      if (voices.length === 0) return; 
      const voice = 
        voices.find((v) => v.lang === 'es-MX') ||
        voices.find((v) => v.lang === 'es-ES') ||
        voices.find((v) => v.lang.startsWith('es'));
      setSpanishVoice(voice || voices[0]);
    };
    getVoices();
    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = getVoices;
    }
  }, []); 

  const speak = useCallback(({ text }) => {
    if (!text || !window.speechSynthesis) return;
    window.speechSynthesis.cancel(); 
    const utterance = new SpeechSynthesisUtterance(text);
    if (spanishVoice) {
      utterance.voice = spanishVoice; 
      utterance.lang = spanishVoice.lang;
    } else { utterance.lang = 'es'; }
    utterance.onstart = () => setSpeaking(true);
    utterance.onend = () => setSpeaking(false);
    utterance.onerror = (e) => {
      console.error("Error de SpeechSynthesis:", e);
      setSpeaking(false);
    };
    window.speechSynthesis.speak(utterance);
  }, [spanishVoice]); 

  const cancel = useCallback(() => {
    if (window.speechSynthesis) {
      setSpeaking(false);
      window.speechSynthesis.cancel();
    }
  }, []);
  
  return { speak, cancel, speaking };
};


function BSpacyHub({ id, onScrollTo }) {
  const [hasConversationStarted, setHasConversationStarted] = useState(false);
  const [spacyState, setSpacyState] = useState('normal');
  const [currentText, setCurrentText] = useState("");
  
  const { speak, cancel, speaking } = useBrowserTTS();

  useEffect(() => {
    const gifUrlsToPreload = [SpacyNormal, SpacyHablando, SpacyPensando, SpacyError];
    gifUrlsToPreload.forEach((url) => {
      const img = new Image();
      img.src = url;
    });
  }, []); 

  const say = useCallback((text) => {
    if (text) {
      setCurrentText(text);
      speak({ text: text });
    }
  }, [speak]);

  useEffect(() => {
    if (speaking) {
      setSpacyState('hablando');
    } else if (!speaking && spacyState === 'hablando') {
      const timerId = setTimeout(() => {
        setSpacyState('normal');
      }, 500);
      return () => clearTimeout(timerId);
    }
  }, [speaking, spacyState]);

  const handleFunnelClick = (type) => {
    cancel();
    setSpacyState('pensando');
    setCurrentText("");
    
    setTimeout(() => {
      let responseText = "";
      if (type === "funciona") {
        responseText = "Spacy Business crea planes de estudio personalizados para cada empleado usando Inteligencia Artificial. Además, te damos un panel de control exclusivo para medir su progreso, horas de estudio y el retorno de inversión de tu empresa en tiempo real.";
      } else if (type === "roi") {
        responseText = "Las empresas que confían en nosotros ven un aumento del cuarenta por ciento en productividad internacional en los primeros 6 meses. Tu inversión se paga sola al cerrar mejores negocios globales, con empleados que hablan inglés con total fluidez.";
      } else if (type === "demo") {
        responseText = "¡Excelente decisión! Ve a la sección de contacto aquí abajo, déjanos tus datos empresariales, y uno de nuestros ejecutivos de cuenta se pondrá en contacto contigo para una demostración personalizada y en vivo.";
        if (onScrollTo) {
          setTimeout(() => onScrollTo('contact'), 8000);
        }
      }
      
      setSpacyState('hablando');
      say(responseText);
    }, 600);
  };

  const handleStartConversation = () => {
    if (hasConversationStarted) return;
    setHasConversationStarted(true);
    const welcomeMessage = "¡Hola, líder empresarial! Soy Spacy Business, el representante de ventas de Inteligencia Artificial. Estoy aquí para mostrarte cómo revolucionaremos el inglés en tu empresa. ¿Qué te gustaría saber?";
    
    setTimeout(() => {
      setSpacyState('hablando');
      say(welcomeMessage);
    }, 500);
  };

  const getCurrentGif = () => {
    switch (spacyState) {
      case 'hablando': return SpacyHablando;
      case 'pensando': return SpacyPensando;
      case 'normal': default: return SpacyNormal;
    }
  };

  return (
    <section className="spacy-business-section" id={id || "spacy"}>
      <div className="container">
        <h2 className="section-title">Conoce a <span className="highlight-blue">Spacy Business</span></h2>
        <p className="section-subtitle">Tu ejecutivo de ventas IA (Embudo interactivo cero tokens)</p>
        
        <div className="spacy-main-card">
          
          <div className="spacy-hero">
            <div className="spacy-avatar-container">
              <svg className="progress-ring" width="400" height="400" viewBox='0 0 400 400'>
                <circle className="progress-ring-bg" cx="200" cy="200" r="190" />
                <circle 
                  className={`progress-ring-bar ${spacyState === 'pensando' ? 'loading' : ''}`} 
                  cx="200" cy="200" r="190" 
                />
              </svg>
              <div className="spacy-avatar-main">
                {/* `key` obliga al navegador a recargar al cambiar de estado.
                    `muted` y `playsInline` no son opcionales: sin los dos, el
                    movil no deja que un video arranque solo. */}
                <video
                  key={getCurrentGif()}
                  src={getCurrentGif()}
                  className="spacy-img"
                  autoPlay
                  loop
                  muted
                  playsInline
                  aria-label="Spacy AI"
                />
                <div className="spacy-glow-effect"></div>
              </div>
            </div> 
          </div> 
          
          <div className="spacy-controls-wrapper">
            
            {hasConversationStarted && currentText && (
              <div className="spacy-response-bubble" style={{ background: 'rgba(255,255,255,0.05)', padding: '15px 20px', borderRadius: '15px', marginBottom: '20px', borderLeft: '4px solid #00f0ff', color: 'var(--text-primary)', fontSize: '1.1rem', fontStyle: 'italic' }}>
                "{currentText}"
              </div>
            )}

            <div 
              className="spacy-interactive-funnel" 
              style={{ 
                display: 'flex', flexDirection: 'column', gap: '12px',
                opacity: hasConversationStarted ? 1 : 0, 
                pointerEvents: hasConversationStarted ? 'auto' : 'none' 
              }}
            >
              <button 
                className="b-hero-cta" 
                style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid rgba(255,255,255,0.2)', width: '100%', textAlign: 'left', padding: '16px 24px', fontSize: '1.1rem', borderRadius: '12px' }} 
                onClick={() => handleFunnelClick('funciona')}
              >
                🏢 ¿Cómo funciona la plataforma para empresas?
              </button>
              <button 
                className="b-hero-cta" 
                style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid rgba(255,255,255,0.2)', width: '100%', textAlign: 'left', padding: '16px 24px', fontSize: '1.1rem', borderRadius: '12px' }} 
                onClick={() => handleFunnelClick('roi')}
              >
                📈 ¿Cuál es el Retorno de Inversión (ROI)?
              </button>
              <button 
                className="b-hero-cta" 
                style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid rgba(255,255,255,0.2)', width: '100%', textAlign: 'left', padding: '16px 24px', fontSize: '1.1rem', borderRadius: '12px' }} 
                onClick={() => handleFunnelClick('demo')}
              >
                📅 ¡Me interesa! Quiero agendar una demo en vivo
              </button>
            </div>
            
            <div className="spacy-status" style={{ opacity: hasConversationStarted ? 1 : 0, pointerEvents: hasConversationStarted ? 'auto' : 'none', marginTop: '20px' }}>
              <span className={`status-dot ${spacyState === 'pensando' ? 'loading' : ''}`}></span>
              {spacyState === 'pensando' ? "Spacy está analizando la respuesta..." : "Haz clic en una opción para escuchar a Spacy Business"}
            </div>

            {!hasConversationStarted && (
              <div className="b-spacy-overlay-card">
                <p className="b-spacy-overlay-text">Tu representante de ventas IA está listo para resolver tus dudas sobre implementación, costos y ROI.</p>
                <button 
                  className="b-spacy-start-button"
                  onClick={handleStartConversation}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
                  Conversar con Spacy Business
                </button>
              </div>
            )}
            
          </div>
        </div>
      </div>
    </section>
  );
}

export default BSpacyHub;